// @flow
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { format as dateFmt } from 'date-fns';

import Modal from '@shared/Modal/Modal';
import Button, { buttonConstants } from '@shared/Button';
import DatePickerField from '_common/components/DateTimePicker/components/DatePickerField';
import SelectField from '@common/components/select-field/SelectField';
import InputField from '@common/components/input-field/InputField';

import DateService from '_common/services/DateService';
import GroupClassSmsExportService from '@groupClass/services/GroupClassSmsExportService';
import { ERROR_IS_BLANK } from '@helpers/TranslationHelpers';

import type { ErrorProps, Error } from '@core/types';

import { FORMAT_DATE_API } from '@app/constants/dates';
import Toast from '../../_common/services/Toast/Toast';

type Props = {
  isActive: boolean,
  toggleModal: () => void,
};

const EXPORT_TYPE_SUMMARY = 'summary';
const EXPORT_TYPE_DETAILED = 'detailed';
const DATE_TYPE_START = 'startDate';
const DATE_TYPE_END = 'endDate';

const ModalSmsExport = (props: Props): React$Node => {
  const { isActive, toggleModal } = props;
  const { t } = useTranslation();

  const [exportType, setExportType] = useState<string>(EXPORT_TYPE_SUMMARY);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [groupClassId, setGroupClassId] = useState<string | null>(null);
  const [errors, setErrors] = useState<Error[]>([]);

  const handleChange = (date: moment$Moment, type: string): void => {
    const dateFormatted = date ? new Date(String(date)) : null;
    if (type === DATE_TYPE_START) {
      setStartDate(dateFormatted);
    } else if (type === DATE_TYPE_END) {
      setEndDate(dateFormatted);
    }
  };

  const handleExportTypeChange = (event: SyntheticEvent<HTMLSelectElement>): void => {
    setExportType(event?.currentTarget?.value);
  };

  const handleSubmit = (): void => {
    const errorsTmp = [];

    if (!startDate) {
      errorsTmp.push({
        propertyPath: DATE_TYPE_START,
        code: ERROR_IS_BLANK,
        message: t('commonError.thisFieldIsMandatory'),
      });
    }

    if (!endDate) {
      errorsTmp.push({
        propertyPath: DATE_TYPE_END,
        code: ERROR_IS_BLANK,
        message: t('commonError.thisFieldIsMandatory'),
      });
    }

    if (exportType === EXPORT_TYPE_DETAILED && !groupClassId) {
      errorsTmp.push({
        propertyPath: 'groupClassId',
        code: ERROR_IS_BLANK,
        message: t('commonError.thisFieldIsMandatory'),
      });
    }

    setErrors(errorsTmp);

    if (errorsTmp.length === 0) {
      const startDateFormatted = dateFmt(new Date(startDate), FORMAT_DATE_API);
      const endDateFormatted = dateFmt(new Date(endDate), FORMAT_DATE_API);
      const params = {
        from: startDateFormatted,
        to: endDateFormatted,
        id: exportType === EXPORT_TYPE_DETAILED ? groupClassId : undefined,
      };

      GroupClassSmsExportService.fetchExport(params, exportType)
        .then(() => {
          toggleModal();
        })
        .catch(() => {
          Toast.error(t('groupClasses.groupClass.peoples.form.genericError'));
        });
    }
  };

  const errorsProps = useMemo((): ErrorProps => {
    const errorsPropsTmp = {};
    errors.forEach((error: Error) => {
      Object.assign(errorsPropsTmp, {
        [`${ error.propertyPath }`]: {
          color: 'is-danger',
          labelProps: { className: 'has-text-danger' },
          error: {
            text: error.message,
            textColor: 'is-danger',
          },
        },
      });
    });
    return errorsPropsTmp;
  }, [errors]);

  const footerModalSmsExport: React$Node = useMemo(
    (): React$Node => (
      <footer className="modal-export-footer">
        <Button type={ buttonConstants.PRIMARY } className="button-modal" onClick={ handleSubmit }>
          { t('common.validate') }
        </Button>
      </footer>
    ),
    [handleSubmit, t],
  );

  const exportTypeOptions = [
    { label: t('groupClasses.modalExportType.summary'), value: EXPORT_TYPE_SUMMARY },
    { label: t('groupClasses.modalExportType.detailed'), value: EXPORT_TYPE_DETAILED },
  ];

  return (
    <Modal
      isActive={ isActive }
      isClipped
      onClose={ toggleModal }
      title={ t('groupClasses.smsExport') }
      footer={ footerModalSmsExport }
      modalContentClassName="modal-export-card"
    >
      <div className="modal-export">
        <p className="modal-export-text">{ t('groupClasses.modalSmsExportText') }</p>
        <SelectField
          name="exportType"
          label={ t('groupClasses.modalExportType') }
          options={ exportTypeOptions }
          onChange={ handleExportTypeChange }
          defaultValue={ exportTypeOptions[0] }
          color={ errorsProps.exportType?.color }
          labelProps={ errorsProps.exportType?.labelProps }
          helpProps={ errorsProps.exportType?.error }
          required
        />
        <DatePickerField
          id="startDate"
          name="startDate"
          placeholder="--/--/----"
          value={ startDate ?? null }
          label={ t('groupClasses.modalStartDate') }
          onChange={ (date) => handleChange(date, 'startDate') }
          color={ errorsProps.startDate?.color }
          labelProps={ errorsProps.startDate?.labelProps }
          helpProps={ errorsProps.startDate?.error }
          required
          maxDate={ endDate ? DateService.removeDayFromDate(endDate, 1) : null }
        />
        <DatePickerField
          id="endDate"
          name="endDate"
          placeholder="--/--/----"
          value={ endDate ?? null }
          label={ t('groupClasses.modalEndDate') }
          onChange={ (date) => handleChange(date, 'endDate') }
          color={ errorsProps.endDate?.color }
          labelProps={ errorsProps.endDate?.labelProps }
          helpProps={ errorsProps.endDate?.error }
          required
          minDate={ startDate ? DateService.addDayToDate(startDate, 1) : null }
        />
        { exportType === EXPORT_TYPE_DETAILED && (
          <InputField
            name="groupClassId"
            placeholder="2025-DOS******"
            label={ t('groupClasses.groupClass.detailed.caseNumber') }
            value={ groupClassId ?? '' }
            onChange={ (e) => setGroupClassId(e.target.value) }
            color={ errorsProps.groupClassId?.color }
            labelProps={ errorsProps.groupClassId?.labelProps }
            helpProps={ errorsProps.groupClassId?.error }
            required
          />
        ) }
      </div>
    </Modal>
  );
};

export default ModalSmsExport;
